import React from "react";
import classNames from "classnames";
import { AccordionRoot } from "@design-system/Accordion/Accordion";
import { Checkbox } from "@design-system/Checkbox/Checkbox";
import { Input } from "@design-system/Input/Input";
import { VStack } from "@design-system/VStack/VStack";
import {
    DETAILS_FIELD_DEFINITIONS,
    FROM_FIELD_DEFINITIONS,
    HEADER_FIELD_DEFINITIONS,
    ITEMS_TABLE_FIELD_DEFINITIONS,
    NOTES_FIELD_DEFINITIONS,
    TO_FIELD_DEFINITIONS,
    TOTALS_FIELD_DEFINITIONS,
    type InvoiceFieldDefinition,
    type InvoiceFieldSetting,
    type InvoiceFieldSettings,
    type InvoiceFieldSettingsDraft,
} from "./invoiceFieldSettings";
import { ItemSettingsContent } from "./ItemSettingsPanel";
import { PanelSection } from "./PanelSection";
import styles from "./InvoiceFieldSettingsPanel.module.scss";

interface FieldSettingsSection {
    kind: "fields";
    id: keyof InvoiceFieldSettingsDraft;
    title: string;
    fields: InvoiceFieldDefinition[];
}

interface AdjustmentSettingsSection {
    kind: "adjustments";
}

// Document top-to-bottom order. `itemsTable` is the line-item column labels;
// `items` (rendered by the adjustments section) is per-line tax, discount,
// and deposit coding.
const SECTIONS: Array<FieldSettingsSection | AdjustmentSettingsSection> = [
    {
        kind: "fields",
        id: "header",
        title: "Header",
        fields: HEADER_FIELD_DEFINITIONS,
    },
    {
        kind: "fields",
        id: "from",
        title: "From",
        fields: FROM_FIELD_DEFINITIONS,
    },
    { kind: "fields", id: "to", title: "To", fields: TO_FIELD_DEFINITIONS },
    // Payment (invoice/PO numbers, Terms, invoice/due dates) and Notes
    // (Memo, Payment instructions) were previously one "Payment & notes"
    // section (PRO-12178). Notes sits last, after Totals.
    {
        kind: "fields",
        id: "details",
        title: "Payment",
        fields: DETAILS_FIELD_DEFINITIONS,
    },
    {
        kind: "fields",
        id: "itemsTable",
        title: "Items",
        fields: ITEMS_TABLE_FIELD_DEFINITIONS,
    },
    { kind: "adjustments" },
    {
        kind: "fields",
        id: "totals",
        title: "Totals",
        fields: TOTALS_FIELD_DEFINITIONS,
    },
    {
        kind: "fields",
        id: "notes",
        title: "Notes",
        fields: NOTES_FIELD_DEFINITIONS,
    },
];

export type InvoiceFieldSettingsOpenSection =
    | keyof InvoiceFieldSettingsDraft
    | "items";

interface Props {
    draft: InvoiceFieldSettingsDraft;
    onChange: (draft: InvoiceFieldSettingsDraft) => void;
    initialOpenSection?: InvoiceFieldSettingsOpenSection;
    entityId?: number;
}

function SettingsTable({
    fields,
    settings,
    onChange,
}: {
    fields: InvoiceFieldDefinition[];
    settings: InvoiceFieldSettings;
    onChange: (settings: InvoiceFieldSettings) => void;
}) {
    const updateField = (
        fieldId: string,
        patch: Partial<InvoiceFieldSetting>,
    ) => {
        const current = settings[fieldId] ?? {
            label: "",
            isIncludedOnPdf: true,
        };
        onChange({ ...settings, [fieldId]: { ...current, ...patch } });
    };

    return (
        <table className={styles.table}>
            <thead>
                <tr>
                    <th className={styles.fieldColumn}>Field</th>
                    <th>Label</th>
                    <th className={styles.includeColumn}>
                        Include on invoice PDF
                    </th>
                </tr>
            </thead>
            <tbody>
                {fields.map((field) => {
                    const setting = settings[field.id];
                    const isIncluded =
                        setting?.isIncludedOnPdf ?? field.isIncludedByDefault;
                    return (
                        <tr
                            key={field.id}
                            className={classNames(
                                styles.fieldRow,
                                !field.canHide && styles.fieldRowLocked,
                            )}
                            onClick={
                                field.canHide
                                    ? () =>
                                          updateField(field.id, {
                                              isIncludedOnPdf: !isIncluded,
                                          })
                                    : undefined
                            }
                        >
                            <td className={styles.fieldColumn}>{field.name}</td>
                            {/* Only the input and the checkbox itself swallow
                                clicks (typing must not toggle, and the
                                checkbox's own change handler must not
                                double-fire with the row's); the rest of their
                                cells still toggles through the row. */}
                            <td>
                                <Input
                                    size="md"
                                    className={styles.labelInput}
                                    value={setting?.label ?? ""}
                                    maxLength={100}
                                    aria-label={`${field.name} label`}
                                    onClick={(event) => event.stopPropagation()}
                                    onChange={(event) =>
                                        updateField(field.id, {
                                            label: event.target.value,
                                        })
                                    }
                                />
                            </td>
                            <td className={styles.includeColumn}>
                                {field.canHide ? (
                                    <span
                                        className={styles.checkboxWrap}
                                        onClick={(event) =>
                                            event.stopPropagation()
                                        }
                                    >
                                        <Checkbox
                                            checked={isIncluded}
                                            aria-label={`Include ${field.name} on invoice PDF`}
                                            onCheckedChange={(checked) =>
                                                updateField(field.id, {
                                                    isIncludedOnPdf: checked,
                                                })
                                            }
                                        />
                                    </span>
                                ) : null}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

// Shared settings page: PDF field labels/visibility (From, To, Payment) and
// per-line Tax / Discount / Deposit. Same content for the invoice swap-in
// view and the template accordion section.
export const InvoiceFieldSettingsContent: React.FC<Props> = ({
    draft,
    onChange,
    initialOpenSection = "from",
    entityId,
}) => (
    <AccordionRoot<InvoiceFieldSettingsOpenSection>
        defaultValue={[initialOpenSection]}
    >
        <VStack gap={16} className={styles.content}>
            {SECTIONS.map((section) =>
                section.kind === "adjustments" ? (
                    <PanelSection
                        key="items"
                        label="Tax, discount & deposit"
                        value="items"
                        hasFlushBody
                    >
                        <ItemSettingsContent entityId={entityId} />
                    </PanelSection>
                ) : (
                    <PanelSection
                        key={section.id}
                        label={section.title}
                        value={section.id}
                        hasFlushBody
                    >
                        <SettingsTable
                            fields={section.fields}
                            settings={draft[section.id]}
                            onChange={(settings) =>
                                onChange({
                                    ...draft,
                                    [section.id]: settings,
                                })
                            }
                        />
                    </PanelSection>
                ),
            )}
        </VStack>
    </AccordionRoot>
);
